import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState("");

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data.templates)).catch(() => toast.error("Failed to load templates"));
  }, []);

  const startCreate = (tpl) => {
    setSelected(tpl);
    setTitle(tpl?.default_landing?.headline || tpl?.name || "");
  };

  const create = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      const r = await api.post("/lead-magnets", { title, template_id: selected.id });
      toast.success("Lead magnet created");
      navigate(`/builder/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <div className="label-cap mb-2">// Onboarding</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">Let's build your first lead page.</h1>
        <p className="text-zinc-600 max-w-xl">Pick your content type. Build your page. Start growing your list today.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((tpl) => (
          <div key={tpl.id} className="card-brutal !p-0 overflow-hidden flex flex-col" data-testid={`template-card-${tpl.id}`}>
            <div className="aspect-[4/3] bg-zinc-100 border-b-2 border-black overflow-hidden">
              <img src={tpl.thumbnail} alt={tpl.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="label-cap mb-2">{tpl.category}</div>
              <h3 className="font-display font-bold text-xl mb-2">{tpl.name}</h3>
              <p className="text-sm text-zinc-600 flex-1">{tpl.description}</p>
              <button onClick={() => startCreate(tpl)} className="btn-brutal mt-4 w-full" data-testid={`use-template-${tpl.id}`}>
                Use This Template <ArrowRight size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50">
          <div className="card-brutal !p-8 max-w-md w-full bg-white" data-testid="template-modal">
            <div className="label-cap mb-2">Template · {selected.name}</div>
            <h2 className="font-display font-black text-3xl mb-4">Name your campaign</h2>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="input-brutal mb-4" placeholder="e.g. Free SEO Checklist" data-testid="new-lm-title-input" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setSelected(null)} className="btn-brutal-outline" data-testid="cancel-create-btn">Cancel</button>
              <button onClick={create} disabled={creating} className="btn-brutal" data-testid="confirm-create-btn">
                {creating ? "Creating…" : "Create & Edit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
